import { api } from '@/lib/api';
import { requestStudyNoteLearning } from './studyNotebookOperationsApi';

import type { StudyAnalysisRefund, StudyAnalysisStatus } from './studyAnalysisApi';

/**
 * Next steps after a page analysis (study-capture-fixes-prd.md F-10, G-01 to G-05). The server
 * owns every price, charge, refund and Review date; the app shows them. Check results carry a
 * verdict and one pointing hint, never the correct answer. A practice answer is returned only
 * after the learner tried that question at least once.
 */

// ── F-10 ready notices ──────────────────────────────────────────────────────

export type StudyFinishedAnalysisStatus = Extract<
  StudyAnalysisStatus,
  'succeeded' | 'partial' | 'failed'
>;

/** A finished analysis the learner has not opened yet (last 7 days, at most 5, newest first). */
export interface StudyUnseenAnalysis {
  analysis_id: string;
  document_id: string;
  file_name: string;
  /** Learner-chosen material name; it wins over the topic and the file name. */
  display_name?: string | null;
  status: StudyFinishedAnalysisStatus;
  topic: string | null;
  completed_at: string;
  refund: StudyAnalysisRefund;
}

export const STUDY_UNSEEN_ANALYSES_QUERY_KEY = ['study-analyses-unseen'] as const;

export const listUnseenStudyAnalyses = () =>
  api<{ items: StudyUnseenAnalysis[] }>('/study/analyses/unseen');

/** Idempotent. While the analysis is still running the server records nothing. */
export const markStudyAnalysisSeen = (analysisId: string) =>
  api<{ analysis_id: string; status: StudyAnalysisStatus; learner_seen_at: string | null }>(
    `/study/analyses/${encodeURIComponent(analysisId)}/seen`,
    { method: 'POST' },
  );

// ── G-03 / G-05 Review queue ────────────────────────────────────────────────

export type StudyAnalysisReviewSource =
  | 'learner_request'
  | 'marked_wrong'
  | 'answer_check'
  | 'practice_wrong';

/** A Review queue item for an analysed material or one of its questions. */
export interface StudyAnalysisReviewItem {
  kind: 'analysis';
  self_assessment?: boolean;
  practice_set_id?: string | null;
  id: string;
  analysis_id: string;
  document_id: string;
  document_name: string;
  question_id: string | null;
  /** Null when the learner removed the question later. */
  question_number: string | null;
  topic: string | null;
  subject: { subject_key: string | null; display_name: string } | null;
  source: StudyAnalysisReviewSource;
  created_at: string;
  review_due_at: string;
  due: boolean;
}

/** "Remind me to review in 3 days": the whole material, or one question. Free; deduplicated. */
export const remindStudyAnalysisReview = (analysisId: string, questionId?: string | null) =>
  api<{ item: StudyAnalysisReviewItem; created: boolean }>(
    `/study/analyses/${encodeURIComponent(analysisId)}/review`,
    { method: 'POST', body: questionId ? { question_id: questionId } : {} },
  );

/**
 * "Done" on a Review item: it leaves Review and the Home count. Free; no AI; a repeat keeps the
 * first time. Queueing the same material or question again later re-opens it.
 */
export const completeStudyAnalysisReview = (itemId: string) =>
  api<{ id: string; reviewed_at: string }>(
    `/study/review/analysis-items/${encodeURIComponent(itemId)}/done`,
    { method: 'POST' },
  );

/** G-05: every question marked wrong on a marked test goes to Review. Free; no AI. */
export const queueMarkedWrongReview = (analysisId: string) =>
  api<{
    analysis_id: string;
    added: number;
    already_queued: number;
    items: StudyAnalysisReviewItem[];
  }>(`/study/analyses/${encodeURIComponent(analysisId)}/marked-wrong/review`, { method: 'POST' });

// ── G-01 Check my answers ───────────────────────────────────────────────────

export type StudyAnswerVerdict = 'correct' | 'incorrect' | 'incomplete' | 'cant_tell';

export interface StudyAnswerCheck {
  id: string;
  question_id: string;
  practice_set_id: string | null;
  status: 'pending' | 'checked' | 'failed';
  verdict: StudyAnswerVerdict | null;
  /** One pointing hint; never the answer or a full method. */
  hint: string | null;
  student_answer_text: string;
  stars: number;
  stars_refunded: number;
  refunded: boolean;
  error_code: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface StudyAnswerCheckQuoteItem {
  question_id: string;
  number: string;
  text: string;
  kind: string;
  /** The learner's answer as read from the page, after their own text corrections. */
  student_answer: string | null;
  /** Page blocks of the answer, `p<page index>:<block id>`. */
  answer_block_ids: string[];
  checkable: boolean;
}

export interface StudyAnswerCheckQuote {
  analysis_id: string;
  stars_per_question: number;
  checkable_questions: number;
  stars_total: number;
  model: string;
  items: StudyAnswerCheckQuoteItem[];
}

export interface StudyAnswerCheckResult {
  analysis_id: string;
  stars_per_question: number;
  stars_charged: number;
  stars_refunded: number;
  items: StudyAnswerCheck[];
  /** Listed questions without a learner answer: not checked and not charged. */
  skipped_question_ids: string[];
}

/** Free: what would be checked and the price. Never calls AI. */
export const quoteStudyAnswerChecks = (analysisId: string) =>
  api<StudyAnswerCheckQuote>(
    `/study/analyses/${encodeURIComponent(analysisId)}/answer-checks/quote`,
    { method: 'POST' },
  );

/** One debit for every checked question; failed or rejected questions are refunded each. */
export const createStudyAnswerChecks = (
  analysisId: string,
  input: { question_ids: string[]; expected_stars: number; idempotency_key: string },
) =>
  api<StudyAnswerCheckResult>(`/study/analyses/${encodeURIComponent(analysisId)}/answer-checks`, {
    method: 'POST',
    body: input,
  });

/** Free: the newest check of each question still in the analysis. */
export const latestStudyAnswerChecks = (analysisId: string) =>
  api<{ analysis_id: string; items: StudyAnswerCheck[] }>(
    `/study/analyses/${encodeURIComponent(analysisId)}/answer-checks/latest`,
  );

// ── G-02 Practice sets ──────────────────────────────────────────────────────

export type StudyPracticeKind = 'working' | 'short_answer' | 'multiple_choice';
export type StudyPracticeOption = 'A' | 'B' | 'C' | 'D';
export const STUDY_PRACTICE_OPTIONS: readonly StudyPracticeOption[] = ['A', 'B', 'C', 'D'];

export interface StudyPracticeQuestion {
  knowledge_ids?: string[];
  knowledge_versions?: Record<string, number>;
  knowledge_notebook_version?: number | null;
  knowledge_notebook_id?: string | null;
  /** `pq1` to `pq5`. */
  id: string;
  kind: StudyPracticeKind;
  stem: string;
  /** Four options for multiple choice; null otherwise. */
  options: string[] | null;
  attempts: number;
  answer_available: boolean;
  /** Null until the learner tried this question at least once. */
  answer: {
    answer: string;
    correct_option: StudyPracticeOption | null;
    worked_solution: string;
  } | null;
  latest_attempt?: StudyAnswerCheck | null;
}

export interface StudyPracticeSet {
  id: string;
  /** The analysis version the set was made from; attempts go to this analysis. */
  analysis_id: string;
  document_id: string;
  status: 'generating' | 'ready' | 'failed';
  question_kinds: StudyPracticeKind[];
  stars_charged: number;
  stars_refunded: number;
  refunded: boolean;
  /** null: saved questions remain readable, but paid checks cannot currently be quoted. */
  check_stars_per_attempt: number | null;
  error_code: string | null;
  created_at: string;
  completed_at: string | null;
  questions: StudyPracticeQuestion[];
}

export interface StudyPracticeSetQuote {
  analysis_id: string;
  stars: number;
  question_count: number;
  question_kinds: StudyPracticeKind[];
  check_stars_per_attempt: number;
  model: string;
}

export interface StudyPracticeAttemptResult {
  practice_set_id: string;
  attempt: StudyAnswerCheck;
  question: Omit<StudyPracticeQuestion, 'latest_attempt'>;
}

export const quoteStudyPracticeSet = (analysisId: string) =>
  requestStudyNoteLearning<StudyPracticeSetQuote>(
    `/study/analyses/${encodeURIComponent(analysisId)}/practice-sets/quote`,
    { method: 'POST' },
  );

export const createStudyPracticeSet = (
  analysisId: string,
  input: { expected_stars: number; idempotency_key: string },
) =>
  requestStudyNoteLearning<StudyPracticeSet>(
    `/study/analyses/${encodeURIComponent(analysisId)}/practice-sets`,
    {
      method: 'POST',
      body: input,
    },
  );

/** Free: the newest 20 sets of the material, across every analysis version. */
export const listStudyPracticeSets = (analysisId: string, requestedSetId?: string | null) =>
  requestStudyNoteLearning<{ document_id: string; items: StudyPracticeSet[] }>(
    `/study/analyses/${encodeURIComponent(analysisId)}/practice-sets${requestedSetId ? `?set_id=${encodeURIComponent(requestedSetId)}` : ''}`,
  );

/**
 * One attempt. Send exactly one of `answer_text` and `choice`. Multiple choice is free
 * (`expected_stars: 0`); other kinds are one paid check.
 */
export const createStudyPracticeAttempt = (
  set: Pick<StudyPracticeSet, 'id' | 'analysis_id'>,
  questionId: string,
  input:
    | { answer_text: string; expected_stars: number; idempotency_key: string }
    | { choice: StudyPracticeOption; expected_stars: number; idempotency_key: string },
) =>
  requestStudyNoteLearning<StudyPracticeAttemptResult>(
    `/study/analyses/${encodeURIComponent(set.analysis_id)}/practice-sets/${encodeURIComponent(set.id)}/questions/${encodeURIComponent(questionId)}/attempts`,
    { method: 'POST', body: input },
  );

// ── Query keys ──────────────────────────────────────────────────────────────

export const answerCheckQuoteKey = (analysisId: string, kidId: string | null) =>
  ['study-answer-check-quote', kidId, analysisId] as const;
export const answerCheckLatestKey = (analysisId: string, kidId: string | null) =>
  ['study-answer-check-latest', kidId, analysisId] as const;
export const practiceSetQuoteKey = (analysisId: string, kidId: string | null) =>
  ['study-practice-set-quote', kidId, analysisId] as const;
export const practiceSetsKey = (analysisId: string, kidId: string | null) =>
  ['study-practice-sets', kidId, analysisId] as const;
export const STUDY_REVIEW_QUERY_KEY = ['study-review'] as const;
